import React from 'react';

type SelectedType = 'articles' | 'events' | 'albums' | 'canticles' | 'books';

interface Filters {
  title?: string;
  publishDateFrom?: string;
  publishDateTo?: string;
  status?: string;
}

interface FilterBarProps {
  type: SelectedType;
  filters: Filters;
  onFiltersChange: (filters: Partial<Filters>) => void;
  onClearFilters: () => void;
  onCreateClick: () => void;
}

const getStatusOptions = (type: SelectedType): { value: string; label: string }[] => {
  switch (type) {
    case 'articles':
      return [
        { value: '', label: 'Tous les statuts' },
        { value: 'draft', label: 'Brouillon' },
        { value: 'published', label: 'Publié' },
        { value: 'archived', label: 'Archivé' },
      ];
    case 'events':
      return [
        { value: '', label: 'Tous les statuts' },
        { value: 'draft', label: 'Brouillon' },
        { value: 'published', label: 'Publié' },
        { value: 'cancelled', label: 'Annulé' },
      ];
    case 'albums':
      return [
        { value: '', label: 'Tous les statuts' },
        { value: 'draft', label: 'Brouillon' },
        { value: 'published', label: 'Publié' },
        { value: 'private', label: 'Privé' },
      ];
    case 'canticles':
      return [
        { value: '', label: 'Tous les statuts' },
        { value: 'draft', label: 'Brouillon' },
        { value: 'published', label: 'Publié' },
        { value: 'archived', label: 'Archivé' },
      ];
    case 'books':
      return [
        { value: '', label: 'Tous les statuts' },
        { value: 'draft', label: 'Brouillon' },
        { value: 'published', label: 'Publié' },
      ];
    default:
      return [];
  }
};

const getTypeLabel = (type: SelectedType): string => {
  const labels: Record<SelectedType, string> = {
    articles: 'Article',
    events: 'Événement',
    albums: 'Album',
    canticles: 'Cantique',
    books: 'Édition',
  };
  return labels[type];
};

export const FilterBar: React.FC<FilterBarProps> = ({
  type,
  filters,
  onFiltersChange,
  onClearFilters,
  onCreateClick,
}) => {
  const hasActiveFilters =
    filters.title || filters.publishDateFrom || filters.publishDateTo || filters.status;

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6'>
      <div className='flex flex-col lg:flex-row gap-4 items-stretch lg:items-end'>
        {/* Title Filter */}
        <div className='flex-1 w-full lg:w-auto'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Titre
          </label>
          <input
            type='text'
            value={filters.title || ''}
            onChange={(e) => onFiltersChange({ title: e.target.value || undefined })}
            placeholder='Rechercher par titre...'
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          />
        </div>

        {/* Publish Date From */}
        <div className='w-full lg:w-auto'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Date de publication (du)
          </label>
          <input
            type='date'
            value={filters.publishDateFrom || ''}
            onChange={(e) =>
              onFiltersChange({ publishDateFrom: e.target.value || undefined })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          />
        </div>

        {/* Publish Date To */}
        <div className='w-full lg:w-auto'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Date de publication (au)
          </label>
          <input
            type='date'
            value={filters.publishDateTo || ''}
            onChange={(e) =>
              onFiltersChange({ publishDateTo: e.target.value || undefined })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          />
        </div>

        {/* Status Filter */}
        <div className='w-full lg:w-auto'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Statut
          </label>
          <select
            value={filters.status || ''}
            onChange={(e) => onFiltersChange({ status: e.target.value || undefined })}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          >
            {getStatusOptions(type).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Action Buttons */}
        <div className='flex gap-2 w-full lg:w-auto items-end'>
          <button
            onClick={onClearFilters}
            title='Effacer les filtres'
            aria-label='Effacer les filtres'
            className={`shrink-0 p-2 text-gray-500 bg-gray-100 rounded-md hover:bg-gray-200 hover:text-gray-700 transition-colors ${
              hasActiveFilters ? '' : 'invisible'
            }`}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='w-4 h-4'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}
            >
              <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
          <button
            onClick={onCreateClick}
            className='flex-1 lg:flex-none px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap'
          >
            + Créer {getTypeLabel(type)}
          </button>
        </div>
      </div>
    </div>
  );
};
